/** 静态图片调色配方。坐标与曲线均归一化，供预览、worker 和导出共同使用。 */
export type CurvePoint = { x: number; y: number };
export const GRADE_CURVES = [
  "master",
  "red",
  "green",
  "blue",
  "hueSaturation",
  "hueLightness",
  "lumaSaturation",
] as const;
export type GradeCurve = (typeof GRADE_CURVES)[number];
export const GRADE_WHEELS = [
  "shadows",
  "midtones",
  "highlights",
  "offset",
] as const;
export type GradeWheel = (typeof GRADE_WHEELS)[number];
export type WheelValue = {
  red: number;
  green: number;
  blue: number;
  luminance: number;
};
export type CubeLut = {
  name: string;
  size: number;
  domainMin: number[];
  domainMax: number[];
  data: number[];
};
export type GradeMaskShape =
  "none" | "linear" | "mirror" | "ellipse" | "rectangle";
export interface ImageColorGrade {
  tint?: number;
  vibrance?: number;
  brilliance?: number;
  sharpen?: number;
  clarity?: number;
  grain?: number;
  fade?: number;
  vignette?: number;
  curves?: Partial<Record<GradeCurve, CurvePoint[]>>;
  wheels?: Partial<Record<GradeWheel, Partial<WheelValue>>>;
  wheelMode?: "primary" | "log";
  wheelStrength?: number;
  lut?: CubeLut | null;
  lutStrength?: number;
  skinProtection?: number;
  mask?: {
    shape?: GradeMaskShape;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    rotation?: number;
    feather?: number;
    invert?: boolean;
  };
}
export interface NormalizedImageColorGrade {
  tint: number;
  vibrance: number;
  brilliance: number;
  sharpen: number;
  clarity: number;
  grain: number;
  fade: number;
  vignette: number;
  curves: Record<GradeCurve, CurvePoint[]>;
  wheels: Record<GradeWheel, WheelValue>;
  wheelMode: "primary" | "log";
  wheelStrength: number;
  lut: CubeLut | null;
  lutStrength: number;
  skinProtection: number;
  mask: Required<NonNullable<ImageColorGrade["mask"]>>;
}
const clamp = (n: number, low = 0, high = 1) =>
  Math.max(low, Math.min(high, n));
const finite = (
  n: number | undefined,
  initial: number,
  low: number,
  high: number,
) => (Number.isFinite(n) ? clamp(n!, low, high) : initial);
export const identityCurve = (): CurvePoint[] => [
  { x: 0, y: 0 },
  { x: 1, y: 1 },
];
export const flatCurve = (): CurvePoint[] => [
  { x: 0, y: 0.5 },
  { x: 1, y: 0.5 },
];

export function normalizeGradeCurve(
  points: CurvePoint[] | undefined,
  tonal: boolean,
): CurvePoint[] {
  const defaults = tonal ? identityCurve() : flatCurve();
  if (!points?.length) return defaults;
  const ordered = new Map<number, number>();
  for (const p of points)
    if (Number.isFinite(p.x) && Number.isFinite(p.y))
      ordered.set(clamp(p.x), clamp(p.y));
  const sorted = [...ordered]
    .sort(([a], [b]) => a - b)
    .map(([x, y]) => ({ x, y }));
  if (!sorted.length) return defaults;
  if (sorted[0].x !== 0) sorted.unshift(defaults[0]);
  if (sorted[sorted.length - 1].x !== 1) sorted.push(defaults[1]);
  return sorted;
}

export function normalizeImageColorGrade(
  grade?: ImageColorGrade | null,
): NormalizedImageColorGrade {
  const curves = Object.fromEntries(
    GRADE_CURVES.map((key, i) => [
      key,
      normalizeGradeCurve(grade?.curves?.[key], i < 4),
    ]),
  ) as NormalizedImageColorGrade["curves"];
  const wheels = Object.fromEntries(
    GRADE_WHEELS.map((key) => [
      key,
      Object.fromEntries(
        ["red", "green", "blue", "luminance"].map((channel) => [
          channel,
          finite(grade?.wheels?.[key]?.[channel as keyof WheelValue], 0, -1, 1),
        ]),
      ),
    ]),
  ) as NormalizedImageColorGrade["wheels"];
  return {
    tint: finite(grade?.tint, 0, -100, 100),
    vibrance: finite(grade?.vibrance, 0, -100, 100),
    brilliance: finite(grade?.brilliance, 0, -100, 100),
    sharpen: finite(grade?.sharpen, 0, 0, 100),
    clarity: finite(grade?.clarity, 0, 0, 100),
    grain: finite(grade?.grain, 0, 0, 100),
    fade: finite(grade?.fade, 0, 0, 100),
    vignette: finite(grade?.vignette, 0, -100, 100),
    curves,
    wheels,
    wheelMode: grade?.wheelMode === "log" ? "log" : "primary",
    wheelStrength: finite(grade?.wheelStrength, 100, 0, 100),
    lut: grade?.lut ?? null,
    lutStrength: finite(grade?.lutStrength, 100, 0, 100),
    skinProtection: finite(grade?.skinProtection, 0, 0, 100),
    mask: {
      shape: grade?.mask?.shape ?? "none",
      x: finite(grade?.mask?.x, 0.5, 0, 1),
      y: finite(grade?.mask?.y, 0.5, 0, 1),
      width: finite(grade?.mask?.width, 0.7, 0.001, 2),
      height: finite(grade?.mask?.height, 0.7, 0.001, 2),
      rotation: finite(grade?.mask?.rotation, 0, -180, 180),
      feather: finite(grade?.mask?.feather, 0.2, 0, 1),
      invert: grade?.mask?.invert === true,
    },
  };
}

export function hasImageColorGrade(grade: NormalizedImageColorGrade): boolean {
  return (
    grade.tint !== 0 ||
    grade.vibrance !== 0 ||
    grade.brilliance !== 0 ||
    grade.sharpen !== 0 ||
    grade.clarity !== 0 ||
    grade.grain !== 0 ||
    grade.fade !== 0 ||
    grade.vignette !== 0 ||
    !!(grade.lut && grade.lutStrength) ||
    GRADE_CURVES.some((key, i) =>
      grade.curves[key].some((p) => Math.abs(p.y - (i < 4 ? p.x : 0.5)) > 1e-8),
    ) ||
    (grade.wheelStrength > 0 &&
      GRADE_WHEELS.some((key) =>
        Object.values(grade.wheels[key]).some((v) => v !== 0),
      ))
  );
}

const lutSignatures = new WeakMap<CubeLut, string>();
export function getImageColorGradeSignature(
  grade: NormalizedImageColorGrade,
): string {
  const { lut, ...parameters } = grade;
  if (!lut) return JSON.stringify(parameters);
  let signature = lutSignatures.get(lut);
  if (!signature) {
    // 编辑配方不可变；只在首次见到 LUT 时扫描大型格点数据。
    let left = 2166136261,
      right = 5381;
    const source = JSON.stringify(lut);
    for (let i = 0; i < source.length; i++) {
      left = Math.imul(left ^ source.charCodeAt(i), 16777619);
      right = Math.imul(right, 33) ^ source.charCodeAt(i);
    }
    signature = `${source.length}:${left >>> 0}:${right >>> 0}`;
    lutSignatures.set(lut, signature);
  }
  return `${JSON.stringify(parameters)}|lut:${signature}`;
}

/** .cube 中 R 最快变化；文件内容永远只作为数据解析。 */
export function parseCubeLut(source: string, fileName: string): CubeLut {
  let size = 0,
    name = fileName,
    domainMin = [0, 0, 0],
    domainMax = [1, 1, 1];
  const data: number[] = [];
  if (source.length > 24_000_000) throw new Error("lutTooLarge");
  for (const raw of source.split(/\r?\n/)) {
    const line = raw.replace(/#.*/, "").trim();
    if (!line) continue;
    const [key, ...rest] = line.split(/\s+/);
    if (key === "TITLE") {
      name = rest.join(" ").replace(/^"|"$/g, "").slice(0, 120);
      continue;
    }
    const values = rest.map(Number);
    if (key === "LUT_3D_SIZE") {
      size = values[0];
      if (!Number.isInteger(size) || size < 2 || size > 65)
        throw new Error("lutInvalidSize");
    } else if (key === "DOMAIN_MIN" || key === "DOMAIN_MAX") {
      if (values.length !== 3 || !values.every(Number.isFinite))
        throw new Error("lutInvalid");
      if (key === "DOMAIN_MIN") domainMin = values;
      else domainMax = values;
    } else {
      const row = [Number(key), ...values];
      if (row.length !== 3 || !row.every(Number.isFinite))
        throw new Error("lutInvalid");
      data.push(...row);
      if (data.length > 65 ** 3 * 3) throw new Error("lutTooLarge");
    }
  }
  if (
    !size ||
    data.length !== size ** 3 * 3 ||
    domainMax.some((v, i) => v <= domainMin[i])
  )
    throw new Error("lutInvalid");
  return { name, size, domainMin, domainMax, data };
}

// 单调 Hermite 插值：不在控制点之间引入额外极值。
export function sampleGradeCurve(points: CurvePoint[], x: number): number {
  let i = 0;
  while (i < points.length - 2 && x > points[i + 1].x) i++;
  const a = points[i],
    b = points[i + 1],
    dx = b.x - a.x;
  if (dx <= 0) return a.y;
  const slope = (b.y - a.y) / dx;
  const tangent = (index: number) => {
    if (index === 0 || index === points.length - 1) return slope;
    const left =
      (points[index].y - points[index - 1].y) /
      (points[index].x - points[index - 1].x);
    const right =
      (points[index + 1].y - points[index].y) /
      (points[index + 1].x - points[index].x);
    return left * right <= 0 ? 0 : (2 * left * right) / (left + right);
  };
  const t = clamp((x - a.x) / dx),
    t2 = t * t,
    t3 = t2 * t;
  return clamp(
    (2 * t3 - 3 * t2 + 1) * a.y +
      (t3 - 2 * t2 + t) * dx * tangent(i) +
      (-2 * t3 + 3 * t2) * b.y +
      (t3 - t2) * dx * tangent(i + 1),
  );
}

export function sampleCubeLut(
  lut: CubeLut,
  red: number,
  green: number,
  blue: number,
): number[] {
  const xyz = [red, green, blue].map(
    (v, i) =>
      clamp((v - lut.domainMin[i]) / (lut.domainMax[i] - lut.domainMin[i])) *
      (lut.size - 1),
  );
  const low = xyz.map(Math.floor),
    high = low.map((v) => Math.min(lut.size - 1, v + 1)),
    fraction = xyz.map((v, i) => v - low[i]);
  const output = [0, 0, 0];
  for (let z = 0; z < 2; z++)
    for (let y = 0; y < 2; y++)
      for (let x = 0; x < 2; x++) {
        const weight =
          (x ? fraction[0] : 1 - fraction[0]) *
          (y ? fraction[1] : 1 - fraction[1]) *
          (z ? fraction[2] : 1 - fraction[2]);
        const index =
          ((z ? high[2] : low[2]) * lut.size ** 2 +
            (y ? high[1] : low[1]) * lut.size +
            (x ? high[0] : low[0])) *
          3;
        for (let c = 0; c < 3; c++) output[c] += lut.data[index + c] * weight;
      }
  return output;
}

function smooth(a: number, b: number, x: number) {
  const t = clamp((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}
export function gradeMaskWeight(
  grade: NormalizedImageColorGrade,
  x: number,
  y: number,
  aspect = 1,
): number {
  const m = grade.mask;
  if (m.shape === "none") return 1;
  const angle = (m.rotation * Math.PI) / 180,
    dx = (x - m.x) * aspect,
    dy = y - m.y;
  const u = (dx * Math.cos(angle) + dy * Math.sin(angle)) / aspect,
    v = -dx * Math.sin(angle) + dy * Math.cos(angle);
  const feather = Math.max(0.00001, m.feather);
  let weight: number;
  if (m.shape === "linear") weight = smooth(-feather * 0.5, feather * 0.5, v);
  else {
    const d =
      m.shape === "ellipse"
        ? Math.hypot(u / (m.width * 0.5), v / (m.height * 0.5))
        : m.shape === "mirror"
          ? Math.abs(v) / (m.height * 0.5)
          : Math.max(
              Math.abs(u) / (m.width * 0.5),
              Math.abs(v) / (m.height * 0.5),
            );
    weight = 1 - smooth(1 - feather, 1, d);
  }
  return m.invert ? 1 - weight : weight;
}

/** 以原尺寸比例选择邻域半径，缩略图与原图导出保持视觉尺度一致。 */
function boxBlur(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number,
): Float32Array {
  const horizontal = new Float32Array(width * height * 3),
    output = new Float32Array(width * height * 3);
  for (let y = 0; y < height; y++)
    for (let c = 0; c < 3; c++) {
      let total = 0;
      for (let x = -radius; x <= radius; x++)
        total += data[(y * width + clamp(x, 0, width - 1)) * 4 + c];
      for (let x = 0; x < width; x++) {
        horizontal[(y * width + x) * 3 + c] = total / (radius * 2 + 1);
        total +=
          data[(y * width + clamp(x + radius + 1, 0, width - 1)) * 4 + c] -
          data[(y * width + clamp(x - radius, 0, width - 1)) * 4 + c];
      }
    }
  for (let x = 0; x < width; x++)
    for (let c = 0; c < 3; c++) {
      let total = 0;
      for (let y = -radius; y <= radius; y++)
        total += horizontal[(clamp(y, 0, height - 1) * width + x) * 3 + c];
      for (let y = 0; y < height; y++) {
        output[(y * width + x) * 3 + c] = total / (radius * 2 + 1);
        total +=
          horizontal[
            (clamp(y + radius + 1, 0, height - 1) * width + x) * 3 + c
          ] -
          horizontal[(clamp(y - radius, 0, height - 1) * width + x) * 3 + c];
      }
    }
  return output;
}

export function applyImageColorGrade(
  image: ImageData,
  grade: NormalizedImageColorGrade,
  original?: Uint8ClampedArray,
): void {
  const { data, width, height } = image;
  if (hasImageColorGrade(grade)) {
    const tables = Object.fromEntries(
      GRADE_CURVES.map((key) => [
        key,
        Float32Array.from({ length: 1025 }, (_, i) =>
          sampleGradeCurve(grade.curves[key], i / 1024),
        ),
      ]),
    ) as Record<GradeCurve, Float32Array>;
    const activeCurves = new Set(
      GRADE_CURVES.filter((key, i) =>
        grade.curves[key].some(
          (p) => Math.abs(p.y - (i < 4 ? p.x : 0.5)) > 1e-8,
        ),
      ),
    );
    const curve = (key: GradeCurve, v: number) => {
      const at = clamp(v) * 1024,
        lo = Math.floor(at);
      return (
        tables[key][lo] +
        (tables[key][Math.min(1024, lo + 1)] - tables[key][lo]) * (at - lo)
      );
    };
    const activeWheels =
      grade.wheelStrength > 0 &&
      GRADE_WHEELS.some((key) =>
        Object.values(grade.wheels[key]).some((v) => v !== 0),
      );
    for (let i = 0; i < data.length; i += 4) {
      if (!data[i + 3]) continue;
      let r = data[i] / 255,
        g = data[i + 1] / 255,
        b = data[i + 2] / 255;
      g = clamp(g - (grade.tint / 100) * 0.06);
      r = clamp(r + (grade.tint / 100) * 0.025);
      b = clamp(b + (grade.tint / 100) * 0.025);
      const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      if (grade.brilliance) {
        const delta =
          (grade.brilliance / 100) * 0.22 * (1 - l) * smooth(0.02, 0.2, l);
        r = clamp(r + delta);
        g = clamp(g + delta);
        b = clamp(b + delta);
      }
      const max = Math.max(r, g, b),
        min = Math.min(r, g, b),
        chroma = max - min;
      let hue =
        chroma === 0
          ? 0
          : max === r
            ? ((g - b) / chroma + 6) % 6
            : max === g
              ? (b - r) / chroma + 2
              : (r - g) / chroma + 4;
      hue /= 6;
      const sat = max > 0 ? chroma / max : 0;
      const skin = 1 - smooth(0.04, 0.13, Math.abs(hue - 0.075));
      let saturation =
        1 + (grade.vibrance / 100) * (1 - sat) * (0.9 - skin * 0.35);
      if (activeCurves.has("hueSaturation"))
        saturation *= Math.max(0, 2 * curve("hueSaturation", hue));
      if (activeCurves.has("lumaSaturation"))
        saturation *= Math.max(0, 2 * curve("lumaSaturation", l));
      const hueLightness = activeCurves.has("hueLightness")
        ? (curve("hueLightness", hue) - 0.5) * 0.6
        : 0;
      r = clamp(l + (r - l) * saturation + hueLightness);
      g = clamp(l + (g - l) * saturation + hueLightness);
      b = clamp(l + (b - l) * saturation + hueLightness);
      if (activeWheels) {
        const shadow =
          1 -
          smooth(
            grade.wheelMode === "log" ? 0.05 : 0,
            grade.wheelMode === "log" ? 0.38 : 0.65,
            l,
          );
        const high = smooth(
          grade.wheelMode === "log" ? 0.62 : 0.35,
          grade.wheelMode === "log" ? 0.95 : 1,
          l,
        );
        for (const [key, weight] of [
          ["shadows", shadow],
          ["midtones", Math.max(0, 1 - shadow - high)],
          ["highlights", high],
          ["offset", 1],
        ] as [GradeWheel, number][]) {
          const w = grade.wheels[key],
            power = ((weight * grade.wheelStrength) / 100) * 0.4;
          r = clamp(r + (w.red + w.luminance) * power);
          g = clamp(g + (w.green + w.luminance) * power);
          b = clamp(b + (w.blue + w.luminance) * power);
        }
      }
      if (activeCurves.has("master")) {
        r = curve("master", r);
        g = curve("master", g);
        b = curve("master", b);
      }
      if (activeCurves.has("red")) r = curve("red", r);
      if (activeCurves.has("green")) g = curve("green", g);
      if (activeCurves.has("blue")) b = curve("blue", b);
      if (grade.lut && grade.lutStrength) {
        const rgb = sampleCubeLut(grade.lut, r, g, b),
          strength =
            (grade.lutStrength / 100) *
            (1 - (skin * sat * grade.skinProtection) / 100);
        r += (rgb[0] - r) * strength;
        g += (rgb[1] - g) * strength;
        b += (rgb[2] - b) * strength;
      }
      const fade = (grade.fade / 100) * 0.3;
      r = r * (1 - fade) + fade * 0.65;
      g = g * (1 - fade) + fade * 0.65;
      b = b * (1 - fade) + fade * 0.65;
      data[i] = clamp(r) * 255;
      data[i + 1] = clamp(g) * 255;
      data[i + 2] = clamp(b) * 255;
    }
    if (grade.sharpen || grade.clarity) {
      const fine = grade.sharpen
        ? boxBlur(
            data,
            width,
            height,
            Math.max(1, Math.round(Math.max(width, height) / 1600)),
          )
        : null;
      const broad = grade.clarity
        ? boxBlur(
            data,
            width,
            height,
            Math.max(2, Math.round(Math.max(width, height) / 100)),
          )
        : null;
      for (let i = 0, p = 0; i < data.length; i += 4, p += 3)
        if (data[i + 3])
          for (let c = 0; c < 3; c++) {
            const source = data[i + c];
            data[i + c] =
              source +
              (fine
                ? (((source - fine[p + c]) * grade.sharpen) / 100) * 1.5
                : 0) +
              (broad
                ? (((source - broad[p + c]) * grade.clarity) / 100) * 0.8
                : 0);
          }
    }
    if (grade.vignette || grade.grain)
      for (let y = 0; y < height; y++)
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          if (!data[i + 3]) continue;
          const edge = smooth(
            0.2,
            0.9,
            Math.hypot(
              x / Math.max(1, width - 1) - 0.5,
              y / Math.max(1, height - 1) - 0.5,
            ),
          );
          // 固定归一化图像坐标的噪声，重绘不会闪烁。
          const nx = Math.floor((x / width) * 1600),
            ny = Math.floor((y / height) * 1600);
          let hash =
            Math.imul(nx + 1, 374761393) ^ Math.imul(ny + 1, 668265263);
          hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
          const noise =
            (((((hash ^ (hash >>> 16)) >>> 0) / 4294967295 - 0.5) *
              grade.grain) /
              100) *
            48;
          for (let c = 0; c < 3; c++) {
            const v = data[i + c];
            data[i + c] =
              (grade.vignette >= 0
                ? v * (1 - (edge * grade.vignette) / 100)
                : v + ((255 - v) * edge * -grade.vignette) / 100) + noise;
          }
        }
  }
  if (original && grade.mask.shape !== "none")
    for (let y = 0; y < height; y++)
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4,
          weight = gradeMaskWeight(
            grade,
            (x + 0.5) / width,
            (y + 0.5) / height,
            width / height,
          );
        for (let c = 0; c < 3; c++)
          data[i + c] =
            original[i + c] + (data[i + c] - original[i + c]) * weight;
      }
}
